import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {Outlet, useNavigate, useSearchParams} from 'react-router-dom';
import {fetchActivitiesRequest} from 'APIs/letrusAPI/activities';
import {getCookie} from 'utils/functions/cookies';
import {Activity} from 'APIs/letrusAPI/activities/types';
import {useCreateUserEvent} from 'APIs/letrusAPI/user_events';
import {UserEventType} from 'APIs/letrusAPI/user_events/types';
import FocusModeModal from 'components/FocusModeModal';
import FocusModeExpireModal from 'components/FocusModeExpireModal';
import FocusModeOfflineModal from 'components/FocusModeOfflineModal';
import FocusModeReturnModal from 'components/FocusModeReturnModal';
import useFocusModeTimer from 'features/useFocusModeTimer';
import useBlockUserActions from 'features/useBlockUserActions';
import useFocusModeWarnings from 'features/useFocusModeWarnings';

export interface FocusModeWarnings {
  warning50Shown: boolean;
  warning10Shown: boolean;
}

export interface FocusModeState {
  /** Whether Modo Foco is active for the current learning path */
  isActive: boolean;
  /** Remaining time in seconds — populated by useFocusModeTimer */
  remainingSeconds: number;
  /** Total duration in seconds from learning_path_timer */
  effectiveTimeSeconds: number;
  /** Offset in ms between client clock and server clock */
  serverOffset: number;
  /** Warning flags to avoid showing the same modal twice */
  warnings: FocusModeWarnings;
  /** Whether the timer has expired */
  isExpired: boolean;
  /** Whether the student is currently offline during an active learning path */
  isOffline: boolean;
  /** The current learning path activity, if resolved */
  learningPath: Activity | undefined;
  /** Whether timer is initializing (awaiting server sync) */
  isInitializingTimer: boolean;
}

export interface ExpireModalMessages {
  submittingMessage?: string;
  doneMessage?: string;
}

export interface FocusModeContextValue extends FocusModeState {
  setRemainingSeconds(seconds: number): void;
  setServerOffset(offsetMs: number): void;
  setIsExpired(expired: boolean): void;
  setWarnings(warnings: Partial<FocusModeWarnings>): void;
  setIsInitializingTimer(initializing: boolean): void;
  registerExpireCallback(fn: () => Promise<void>): () => void;
  registerReconnectCallback(fn: () => Promise<void>): () => void;
  /** Override expire modal messages for the current activity. Call on mount; return value restores defaults on unmount. */
  setExpireModalMessages(messages: ExpireModalMessages): () => void;
}

const FocusModeContext = createContext<FocusModeContextValue | null>(null);

/**
 * FocusModeProvider
 *
 * Mounted at route level above all activity containers. Resolves the current
 * learning path from the ?trilha= query param and activates Modo Foco when
 * uses_focus_mode=true. Stays mounted across step navigation — never remounts.
 *
 * Exposes the full FocusMode state via useFocusMode().
 */
function FocusModeProvider(): JSX.Element {
  const [searchParams] = useSearchParams();
  const learningPathId = searchParams.get('trilha');
  const navigate = useNavigate();
  const {data: activities} = fetchActivitiesRequest();

  // Use ref for remainingSeconds to avoid re-rendering the whole tree every second.
  const remainingSecondsRef = useRef(0);
  const [remainingSeconds, _setRemainingSeconds] = useState(0);

  const [serverOffset, setServerOffset] = useState(0);
  const [isExpired, setIsExpired] = useState(false);
  const [isOffline, setIsOffline] = useState(false);
  const [isInitializingTimer, _setIsInitializingTimer] = useState(false);
  const [expirePhase, setExpirePhase] =
    useState<'idle' | 'waiting' | 'submitting' | 'done'>('idle');
  const [expireDoneMessage, setExpireDoneMessage] = useState<string>(
    'Seu tempo de atividade foi encerrado.',
  );
  const [expireSubmittingMessage, setExpireSubmittingMessage] =
    useState<string>('Enviando sua redação...');
  const [callbackResultMessage, setCallbackResultMessage] =
    useState<string | undefined>(undefined);

  const DEFAULT_EXPIRE_MESSAGES: Required<ExpireModalMessages> = {
    submittingMessage: 'Enviando sua redação...',
    doneMessage: 'Seu tempo de atividade foi encerrado.',
  };

  const setExpireModalMessages = useCallback(
    (messages: ExpireModalMessages) => {
      if (messages.submittingMessage !== undefined) {
        setExpireSubmittingMessage(messages.submittingMessage);
      }
      if (messages.doneMessage !== undefined) {
        setExpireDoneMessage(messages.doneMessage);
      }
      // Return cleanup function that restores defaults
      return () => {
        setExpireSubmittingMessage(DEFAULT_EXPIRE_MESSAGES.submittingMessage);
        setExpireDoneMessage(DEFAULT_EXPIRE_MESSAGES.doneMessage);
      };
    },
    [],
  );
  const [warnings, _setWarnings] = useState<FocusModeWarnings>({
    warning50Shown: false,
    warning10Shown: false,
  });

  const sessionCapturedRef = useRef(false);
  const sessionInitialRemainingRef = useRef<number | null>(null);
  const expireCallbacksRef = useRef<Set<() => Promise<void>>>(new Set());
  const reconnectCallbacksRef = useRef<Set<() => Promise<void>>>(new Set());
  const pendingExpireCallbacksRef = useRef(false);
  const {trigger: createUserEvent} = useCreateUserEvent();

  const registerExpireCallback = useCallback((fn: () => Promise<void>) => {
    expireCallbacksRef.current.add(fn);
    return () => {
      expireCallbacksRef.current.delete(fn);
    };
  }, []);

  const registerReconnectCallback = useCallback((fn: () => Promise<void>) => {
    reconnectCallbacksRef.current.add(fn);
    return () => {
      reconnectCallbacksRef.current.delete(fn);
    };
  }, []);

  const learningPathUserId = useMemo<number | null>(() => {
    if (!learningPathId) return null;
    const parsed = Number(learningPathId);
    return Number.isFinite(parsed) ? parsed : null;
  }, [learningPathId]);

  // Resolve the current learning path from the activities list
  const learningPath = useMemo<Activity | undefined>(() => {
    if (learningPathUserId === null || !activities) return undefined;

    const allActivities = Object.values(activities).flat() as Activity[];
    return allActivities.find((activity) => activity.id === learningPathUserId);
  }, [learningPathUserId, activities]);

  const setRemainingSeconds = useCallback((seconds: number) => {
    remainingSecondsRef.current = seconds;
    _setRemainingSeconds(seconds);
  }, []);

  const setWarnings = useCallback((partial: Partial<FocusModeWarnings>) => {
    _setWarnings((prev) => ({...prev, ...partial}));
  }, []);

  const setIsInitializingTimer = useCallback((initializing: boolean) => {
    _setIsInitializingTimer(initializing);
  }, []);

  const handleUserEvent = useCallback(
    async (eventType: UserEventType): Promise<void> => {
      if (!learningPath?.learning_path_instance_id) {
        return;
      }

      await createUserEvent({
        event_type: eventType,
        learning_path_instance: learningPath.learning_path_instance_id,
      });
    },
    [learningPath, createUserEvent],
  );

  const handleUserEventRef = useRef(handleUserEvent);
  handleUserEventRef.current = handleUserEvent;

  const isOfflineRef = useRef(false);
  isOfflineRef.current = isOffline;

  // Read isExpired inside the networkStatus handler without stale closure.
  const isExpiredRef = useRef(false);
  isExpiredRef.current = isExpired;

  // Timestamp of the most recent connection loss; cleared after event is sent.
  const connectionLostAtRef = useRef<string | null>(null);

  const connectionLossInFlightRef = useRef<Promise<void> | null>(null);

  // Consider the learning path active for Focus Mode only when it
  // actually uses_focus_mode and is not in a review/finished/expired state.
  const INACTIVE_STATUSES = ['finished', 'reviewed', 'expired'];
  const isActive =
    !!learningPath?.uses_focus_mode &&
    !INACTIVE_STATUSES.includes(learningPath?.status ?? '');
  const effectiveTimeSeconds = learningPath?.learning_path_timer ?? 0;

  const startedAt = learningPath?.started ?? null;

  useFocusModeTimer({
    isActive,
    startedAt: startedAt ?? '',
    effectiveTimeSeconds,
    completionDeadlineDatetime: learningPath?.completion_deadline,
    endDatetime: learningPath?.end_datetime,
    setRemainingSeconds,
    setServerOffset,
    setIsExpired,
    setIsInitializingTimer,
  });

  useEffect(() => {
    if (!isActive) {
      sessionInitialRemainingRef.current = null;
      sessionCapturedRef.current = false;
    } else if (!sessionCapturedRef.current && remainingSeconds > 0) {
      sessionCapturedRef.current = true;
      sessionInitialRemainingRef.current = remainingSeconds;
    }
  }, [isActive, remainingSeconds]);

  // Listen for connectivity changes only while the learning path is active.
  // On cleanup (isActive → false), reset offline state so a finished learning path
  // does not bleed state into the next session.
  useEffect(() => {
    if (!isActive) {
      setIsOffline(false);
      connectionLostAtRef.current = null;
      return () => undefined;
    }

    // Native 'offline' event fires immediately when the OS detects network loss,
    // with no polling delay. Guard via isOfflineRef prevents overwriting
    // connectionLostAtRef if the heartbeat confirms offline shortly after.
    const handleNativeOffline = () => {
      if (!isOfflineRef.current) {
        isOfflineRef.current = true;
        setIsOffline(true);
        connectionLostAtRef.current = new Date().toISOString();
      }
    };

    const handler = async (e: Event) => {
      const status = (e as CustomEvent<string>).detail;
      if (status === 'offline') {
        // Heartbeat may confirm offline after the native event — idempotent via guard.
        if (!isOfflineRef.current) {
          isOfflineRef.current = true;
          setIsOffline(true);
          connectionLostAtRef.current = new Date().toISOString();
        }
      } else {
        setIsOffline(false);
        if (connectionLostAtRef.current) {
          connectionLossInFlightRef.current = handleUserEventRef
            .current('connection_loss')
            .finally(() => {
              connectionLossInFlightRef.current = null;
            });
          connectionLostAtRef.current = null;
          // Await before proceeding — guarantees connection_loss reaches the
          // backend before the composition PATCH from reconnect callbacks.
          // Catch to prevent a failed POST from aborting the reconnect flow.
          await connectionLossInFlightRef.current.catch(() => undefined);
        }
        // Run reconnect callbacks only if timer has not expired.
        // Use allSettled so individual rejections don't cause unhandled promise rejections.
        if (!isExpiredRef.current) {
          Promise.allSettled(
            Array.from(reconnectCallbacksRef.current).map((fn) => fn()),
          );
        }
        navigator.serviceWorker?.controller?.postMessage({type: 'REPLAY_SYNC'});
      }
    };

    window.addEventListener('offline', handleNativeOffline);
    window.addEventListener('networkStatus', handler);
    return () => {
      window.removeEventListener('offline', handleNativeOffline);
      window.removeEventListener('networkStatus', handler);
    };
  }, [isActive]);

  // Broadcast isActive state via CustomEvent so components outside this provider
  // (e.g. PageWrapper/Menu) can react without prop-drilling.
  // Split into two effects so that count updates never trigger the isActive: false
  // cleanup pulse — which would momentarily leave consumers with wrong state.
  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent('focusModeStatus', {
        detail: {
          isActive,
          focusModeClosedCount: learningPath?.focus_mode_closed_count ?? 0,
        },
      }),
    );
    return () => {
      // Only fires when isActive changes or on unmount — not on count updates.
      window.dispatchEvent(
        new CustomEvent('focusModeStatus', {
          detail: {isActive: false, focusModeClosedCount: 0},
        }),
      );
    };
  }, [isActive]);

  // Keep PageWrapper's count in sync when it changes (e.g. after a return).
  // No cleanup — we never want to dispatch isActive: false from here.
  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent('focusModeStatus', {
        detail: {
          isActive,
          focusModeClosedCount: learningPath?.focus_mode_closed_count ?? 0,
        },
      }),
    );
  }, [isActive, learningPath?.focus_mode_closed_count]);

  // intentionalLogoutRef is set to true when the student explicitly clicks
  // "Sair mesmo assim". The beforeunload handler (Commit 3) checks this ref
  // to avoid sending a duplicate focus_mode_closed event.
  const intentionalLogoutRef = useRef(false);

  // Listen for the focusModeIntentionalLogout CustomEvent dispatched by Menu
  // before calling logout.trigger(). This keeps all Focus Mode logic inside
  // this provider.
  useEffect(() => {
    const handler = () => {
      intentionalLogoutRef.current = true;
      handleUserEvent('focus_mode_closed');
    };
    window.addEventListener('focusModeIntentionalLogout', handler);
    return () =>
      window.removeEventListener('focusModeIntentionalLogout', handler);
  }, [handleUserEvent]);

  // Send focus_mode_closed via fetch+keepalive when the student closes the tab
  // or browser while Focus Mode is active. keepalive ensures the request survives
  // page teardown. Skipped when the logout was intentional (intentionalLogoutRef)
  // to avoid double-firing alongside the explicit POST in focusModeIntentionalLogout.
  // Note: during Focus Mode we intentionally do not show the native beforeunload confirm dialog.
  // Tracking is handled via this keepalive request instead.
  useEffect(() => {
    if (!isActive) return () => undefined;

    const handleBeforeUnload = () => {
      if (intentionalLogoutRef.current) return;
      if (!learningPath?.learning_path_instance_id) return;

      fetch(`${process.env.REACT_APP_API_URL}/api/v1/user_events/`, {
        method: 'POST',
        keepalive: true,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCookie('csrftoken') ?? '',
        },
        body: JSON.stringify({
          event_type: 'focus_mode_closed',
          learning_path_instance: learningPath.learning_path_instance_id,
        }),
      });
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isActive, learningPath?.learning_path_instance_id]);

  const [showReturnModal, setShowReturnModal] = useState(false);
  const reopenedSentRef = useRef(false);

  // Abandonment redirect.
  // focus_mode_status é derivado em runtime pelo backend: não existe status de
  // abandono gravado. Na reabertura (started/closed_count zerados, nova data de
  // fim), ele já é derivado como estado normal. O guard de awaiting_reopening é
  // uma defesa idempotente para garantir que uma redação reaberta nunca caia na
  // tela de abandono (PROF-2145, regra 4.2 #5 / AC #5).
  useEffect(() => {
    if (learningPath?.focus_mode_awaiting_reopening) return;
    if (learningPath?.focus_mode_status === 'abandonment_rule_violated') {
      navigate('/abandono');
    }
  }, [learningPath, navigate]);

  const {blockingMessage, dismissBlockingModal} = useBlockUserActions({
    isActive,
    isExpired,
    learningPathId,
    onUserEventFired: handleUserEvent,
  });

  const {warningMessage, dismissWarning} = useFocusModeWarnings({
    isActive,
    remainingSeconds,
    effectiveTimeSeconds,
    warnings,
    setWarnings,
    sessionInitialRemaining: sessionInitialRemainingRef.current,
  });

  // Shared logic for running expire callbacks and advancing the modal phase.
  const runExpireCallbacks = (callbacks: (() => Promise<void>)[]) => {
    if (callbacks.length === 0) {
      setCallbackResultMessage(undefined);
      setExpirePhase('done');
      return;
    }

    setExpirePhase('submitting');

    Promise.allSettled(
      callbacks.map((fn) => Promise.resolve().then(() => fn())),
    ).then((results) => {
      const anyFailed = results.some((r) => r.status === 'rejected');
      setCallbackResultMessage(
        anyFailed
          ? 'Não foi possível enviar sua redação automaticamente. Seus dados foram salvos localmente.'
          : 'Sua redação foi enviada com sucesso.',
      );
      setExpirePhase('done');
    });
  };

  useEffect(() => {
    if (!isActive || !isExpired) return;

    const callbacks = Array.from(expireCallbacksRef.current);

    if (isOfflineRef.current) {
      // Offline: defer expire callbacks until reconnect.
      pendingExpireCallbacksRef.current = true;
      setExpirePhase('waiting');
      return;
    }

    // Online: execute immediately (existing behaviour).
    runExpireCallbacks(callbacks);
  }, [isExpired, isActive, learningPathId]);

  useEffect(() => {
    if (isOffline || !pendingExpireCallbacksRef.current || !isActive) return;

    pendingExpireCallbacksRef.current = false;
    const callbacks = Array.from(expireCallbacksRef.current);

    (async () => {
      try {
        // Await the in-flight connection_loss POST before sending expire callbacks,
        // guaranteeing server-side ordering (connection_loss arrives before the PATCH).
        await connectionLossInFlightRef.current;
      } catch {
        // A failed connection_loss POST must not prevent expire callbacks from running.
      }
      runExpireCallbacks(callbacks);
    })();
  }, [isOffline, isActive]);

  // Reset all Focus Mode state when navigating to a different learning path
  useEffect(() => {
    setRemainingSeconds(0);
    setServerOffset(0);
    setIsExpired(false);
    setIsOffline(false);
    setWarnings({warning50Shown: false, warning10Shown: false});
    setExpirePhase('idle');
    setExpireDoneMessage('Seu tempo de atividade foi encerrado.');
    setCallbackResultMessage(undefined);
    setShowReturnModal(false);
    sessionInitialRemainingRef.current = null;
    pendingExpireCallbacksRef.current = false;
    connectionLostAtRef.current = null;
    connectionLossInFlightRef.current = null;
    reopenedSentRef.current = false;
    intentionalLogoutRef.current = false;
  }, [
    learningPathId,
    setRemainingSeconds,
    setServerOffset,
    setIsExpired,
    setWarnings,
  ]);

  // PROF-2145 — Reabertura de redação em Modo Foco (tratar como nova tentativa).
  //
  // Toda a mecânica abaixo é DERIVADA dos campos que o backend devolve em
  // GET /activities/ na reabertura:
  //   - started: null                      -> timer conta cheio + modal de instruções reaparece (AC #1, #3)
  //   - focus_mode_closed_count: 0          -> contador de saídas zerado na tela (AC #4)
  //   - focus_mode_awaiting_reopening: true -> dispara modal de retorno e o guard de abandono acima (AC #5)
  //   - focus_mode_status: estado normal (not_started/in_progress), não abandonment_rule_violated/expired (AC #5)
  //   - status: 'opened'/'in_progress'      -> atividade concluída normal, não 'expired' (AC #5)
  //   - feedback_released: false/null       -> devolutiva oculta enquanto o período estiver aberto (AC #6)
  //   - o texto salvo em cache da composition é preservado e recarregado no editor (AC #2)
  useEffect(() => {
    if (!learningPath?.focus_mode_awaiting_reopening) return;
    if (reopenedSentRef.current) return;
    reopenedSentRef.current = true;
    handleUserEventRef.current('focus_mode_reopened');
    setShowReturnModal(true);
  }, [learningPath]);

  const value = useMemo<FocusModeContextValue>(
    () => ({
      isActive,
      remainingSeconds,
      effectiveTimeSeconds,
      serverOffset,
      warnings,
      isExpired,
      isOffline,
      isInitializingTimer,
      learningPath,
      setRemainingSeconds,
      setServerOffset,
      setIsExpired,
      setWarnings,
      setIsInitializingTimer,
      registerExpireCallback,
      registerReconnectCallback,
      setExpireModalMessages,
    }),
    [
      isActive,
      remainingSeconds,
      effectiveTimeSeconds,
      serverOffset,
      warnings,
      isExpired,
      isOffline,
      isInitializingTimer,
      learningPath,
      setRemainingSeconds,
      setServerOffset,
      setIsExpired,
      setWarnings,
      setIsInitializingTimer,
      registerExpireCallback,
      registerReconnectCallback,
      setExpireModalMessages,
    ],
  );

  return (
    <FocusModeContext.Provider value={value}>
      <FocusModeModal
        message={blockingMessage}
        onContinue={dismissBlockingModal}
      />

      <FocusModeModal message={warningMessage} onContinue={dismissWarning} />

      <FocusModeExpireModal
        phase={expirePhase}
        onGoToDashboard={() => navigate('/')}
        submittingMessage={expireSubmittingMessage}
        doneMessage={callbackResultMessage ?? expireDoneMessage}
      />

      {isActive && isOffline && !isExpired && <FocusModeOfflineModal />}

      {showReturnModal && (
        <FocusModeReturnModal
          focusModeClosedCount={learningPath?.focus_mode_closed_count ?? 1}
          onContinue={() => {
            setShowReturnModal(false);
            reopenedSentRef.current = false;
          }}
        />
      )}

      <Outlet />
    </FocusModeContext.Provider>
  );
}

export function isFocusModeFeedbackReleased(activity?: Activity): boolean {
  return activity?.feedback_released === true;
}

/**
 * useFocusMode
 *
 * Exposes the current Modo Foco state to any container inside the learning
 * path route. Throws if used outside FocusModeProvider.
 */
function useFocusMode(): FocusModeContextValue {
  const context = useContext(FocusModeContext);

  if (!context) {
    throw new Error('useFocusMode must be used within a FocusModeProvider');
  }

  return context;
}

export {FocusModeContext, useFocusMode};
export default FocusModeProvider;
